import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Paper,
  Tooltip,
  Badge,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Close as CloseIcon,
  Restore as RestoreIcon,
  ShoppingCart as CartIcon,
  AddShoppingCart as AddToCartIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import { AppHeader } from '../components/app-header';
import { LoadingState } from '../components/loading-state';
import {
  useGetAdminTemplateRevisionsQuery,
  useGetAdminTemplateRevisionQuery,
  useLazyGetAdminTemplateRevisionQuery,
  useGetAdminNoteTemplateQuery,
  useUpdateAdminNoteTemplateMutation,
  type RevisionDetail,
  type RevisionSummary,
} from '../store/hooks';
import { RevertTemplateModal } from '../components/revert-template-modal';
import { SectionCartDrawer, type CartItem } from '../components/section-cart-drawer';

/**
 * Formats a date string to a human-readable format with time
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Column data combining selection and loaded revision */
interface VersionColumn {
  version: number | null;
  revision: RevisionDetail | null;
  isLoading: boolean;
}

/** Get all unique section IDs across all revisions */
function getAllSectionIds(revisions: (RevisionDetail | null)[]): string[] {
  const sectionIds = new Set<string>();
  const sectionOrder: string[] = [];

  revisions.forEach((revision) => {
    if (!revision) return;
    revision.sections.forEach((section) => {
      if (!sectionIds.has(section.id)) {
        sectionIds.add(section.id);
        sectionOrder.push(section.id);
      }
    });
  });

  return sectionOrder;
}

/** Find a section by ID in a revision */
function findSection(revision: RevisionDetail | null, sectionId: string) {
  return revision?.sections.find((s) => s.id === sectionId) ?? null;
}

export default function VersionComparisonPage() {
  const { templateId, providerId: urlProviderId } = useParams<{
    templateId: string;
    providerId?: string;
  }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const providerId = urlProviderId ? decodeURIComponent(urlProviderId) : null;

  // Parse initial versions from URL
  const initialVersions = useMemo(() => {
    const versionsParam = searchParams.get('versions');
    if (versionsParam) {
      return versionsParam
        .split(',')
        .map((v) => parseInt(v, 10))
        .filter((v) => !isNaN(v))
        .slice(0, 3);
    }
    return [];
  }, [searchParams]);

  // Column state - each column has a selected version
  const [columns, setColumns] = useState<(number | null)[]>(() =>
    initialVersions.length >= 2
      ? initialVersions
      : [null, null],
  );

  // Modal state
  const [revertModal, setRevertModal] = useState<{
    open: boolean;
    version: number | null;
    revision: RevisionDetail | null;
  }>({ open: false, version: null, revision: null });

  // Cart state
  const [cart, setCart] = useState<CartItem[]>([]);
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);

  // Fetch revision list
  const {
    data: revisionsData,
    isLoading: isLoadingList,
    refetch: refetchRevisions,
  } = useGetAdminTemplateRevisionsQuery(
    { templateId: templateId ?? '' },
    { skip: !templateId },
  );

  // Fetch current template data (for revert operations)
  const {
    data: templateData,
    refetch: refetchTemplate,
  } = useGetAdminNoteTemplateQuery(
    { templateId: templateId ?? '' },
    { skip: !templateId },
  );

  // Lazy query for fetching individual revisions
  const [fetchRevision] = useLazyGetAdminTemplateRevisionQuery();

  // Update mutation for revert
  const [updateTemplate, { isLoading: isReverting }] =
    useUpdateAdminNoteTemplateMutation();

  // Store loaded revisions
  const [loadedRevisions, setLoadedRevisions] = useState<
    Map<number, RevisionDetail>
  >(new Map());
  const [loadingVersions, setLoadingVersions] = useState<Set<number>>(
    new Set(),
  );

  const revisions = revisionsData?.revisions ?? [];
  const currentVersion = templateData?.revision.version;

  // Initialize columns from current version if not set
  useEffect(() => {
    if (currentVersion && columns.every((c) => c === null) && revisions.length > 0) {
      const latestTwo = revisions.slice(0, 2).map((r) => r.version);
      if (latestTwo.length >= 2) {
        setColumns([latestTwo[0], latestTwo[1]]);
      }
    }
  }, [currentVersion, revisions, columns]);

  // Load revisions when columns change
  useEffect(() => {
    columns.forEach((version) => {
      if (
        version !== null &&
        !loadedRevisions.has(version) &&
        !loadingVersions.has(version)
      ) {
        setLoadingVersions((prev) => new Set(prev).add(version));

        fetchRevision({ templateId: templateId!, version })
          .unwrap()
          .then((data) => {
            setLoadedRevisions((prev) => new Map(prev).set(version, data));
            setLoadingVersions((prev) => {
              const next = new Set(prev);
              next.delete(version);
              return next;
            });
          })
          .catch((err) => {
            console.error(`Failed to load revision ${version}:`, err);
            setLoadingVersions((prev) => {
              const next = new Set(prev);
              next.delete(version);
              return next;
            });
            enqueueSnackbar(`Failed to load version ${version}`, {
              variant: 'error',
            });
          });
      }
    });
  }, [columns, templateId, loadedRevisions, loadingVersions, fetchRevision, enqueueSnackbar]);

  // Build column data
  const columnData: VersionColumn[] = columns.map((version) => ({
    version,
    revision: version !== null ? loadedRevisions.get(version) ?? null : null,
    isLoading: version !== null && loadingVersions.has(version),
  }));

  // Get all section IDs across loaded revisions
  const allSectionIds = useMemo(
    () => getAllSectionIds(columnData.map((c) => c.revision)),
    [columnData],
  );

  // Handlers
  const handleBack = () => {
    if (providerId) {
      navigate(
        `/admin-v2/provider/${encodeURIComponent(providerId)}/templates/${templateId}`,
      );
    } else {
      navigate(`/admin-v2/templates/${templateId}`);
    }
  };

  const handleVersionChange = (columnIndex: number, version: number) => {
    setColumns((prev) => {
      const next = [...prev];
      next[columnIndex] = version;
      return next;
    });
  };

  const handleAddColumn = () => {
    if (columns.length < 3) {
      // Find a version not already selected
      const usedVersions = new Set(columns);
      const available = revisions.find((r) => !usedVersions.has(r.version));
      setColumns((prev) => [...prev, available?.version ?? null]);
    }
  };

  const handleRemoveColumn = (columnIndex: number) => {
    if (columns.length > 2) {
      setColumns((prev) => prev.filter((_, i) => i !== columnIndex));
    }
  };

  const handleRevertClick = (version: number, revision: RevisionDetail) => {
    setRevertModal({ open: true, version, revision });
  };

  const handleRevertConfirm = async () => {
    if (!revertModal.revision || !templateId) return;

    try {
      const revision = revertModal.revision;
      await updateTemplate({
        templateId,
        adminUpdateNoteTemplateRequest: {
          title: revision.title,
          description: revision.description,
          prompt: revision.prompt,
          models: revision.models,
          sections: revision.sections.map((s, i) => ({
            id: s.id,
            title: s.title,
            content: s.content,
            order: i,
            example_output: s.example_output,
            legacy_module_id: s.legacy_module_id,
          })),
        },
      }).unwrap();

      // Refetch data to update cache
      await Promise.all([refetchRevisions(), refetchTemplate()]);

      enqueueSnackbar(
        `Reverted to version ${revertModal.version}. New version created.`,
        { variant: 'success' },
      );
      setRevertModal({ open: false, version: null, revision: null });
      handleBack();
    } catch (error) {
      console.error('Failed to revert:', error);
      enqueueSnackbar('Failed to revert template', { variant: 'error' });
    }
  };

  // Cart handlers
  const isInCart = useCallback(
    (sectionId: string) => cart.some((item) => item.sectionId === sectionId),
    [cart],
  );

  const handleAddToCart = useCallback(
    (sectionId: string, sectionTitle: string, version: number, content: string) => {
      // Remove existing entry for this section if any (replace with new version)
      setCart((prev) => [
        ...prev.filter((item) => item.sectionId !== sectionId),
        { sectionId, sectionTitle, fromVersion: version, content },
      ]);
    },
    [],
  );

  const handleRemoveFromCart = useCallback((sectionId: string) => {
    setCart((prev) => prev.filter((item) => item.sectionId !== sectionId));
  }, []);

  const handleClearCart = useCallback(() => {
    setCart([]);
  }, []);

  const handleApplyCart = useCallback(async () => {
    if (!templateData || !templateId || cart.length === 0) return;

    try {
      const currentSections = templateData.revision.sections ?? [];
      const updatedSections = currentSections.map((s, i) => {
        const cartItem = cart.find((item) => item.sectionId === s.id);
        if (cartItem) {
          return {
            id: s.id,
            title: s.title,
            content: cartItem.content,
            order: i,
            example_output: s.example_output ?? null,
            legacy_module_id: s.legacy_module_id ?? null,
          };
        }
        return {
          id: s.id,
          title: s.title,
          content: s.content,
          order: i,
          example_output: s.example_output ?? null,
          legacy_module_id: s.legacy_module_id ?? null,
        };
      });

      await updateTemplate({
        templateId,
        adminUpdateNoteTemplateRequest: {
          sections: updatedSections,
        },
      }).unwrap();

      // Refetch data to update cache
      await Promise.all([refetchRevisions(), refetchTemplate()]);

      const sectionNames = cart.map((item) => item.sectionTitle).join(', ');
      enqueueSnackbar(
        `Updated ${cart.length} section${cart.length !== 1 ? 's' : ''}: ${sectionNames}`,
        { variant: 'success' },
      );
      setCart([]);
      setCartDrawerOpen(false);
      handleBack();
    } catch (error) {
      console.error('Failed to apply cart changes:', error);
      enqueueSnackbar('Failed to apply changes', { variant: 'error' });
    }
  }, [templateData, templateId, cart, updateTemplate, enqueueSnackbar, handleBack, refetchRevisions, refetchTemplate]);

  if (!templateId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 py-8">
          <Alert severity="error">Missing template ID</Alert>
        </main>
      </div>
    );
  }

  if (isLoadingList) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 py-8">
          <LoadingState message="Loading version history..." />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      <main className="max-w-[1600px] mx-auto px-4 py-6">
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              variant="outlined"
            >
              Back to Editor
            </Button>
            <Typography variant="h5">Compare Template Versions</Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {columns.length < 3 && (
              <Button
                startIcon={<AddIcon />}
                onClick={handleAddColumn}
                variant="outlined"
              >
                Add Column
              </Button>
            )}
            <Tooltip title="Section cart">
              <IconButton
                onClick={() => setCartDrawerOpen(true)}
                color={cart.length > 0 ? 'primary' : 'default'}
                sx={{ border: 1, borderColor: cart.length > 0 ? 'primary.main' : 'divider' }}
              >
                <Badge badgeContent={cart.length} color="primary">
                  <CartIcon />
                </Badge>
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Version Column Headers */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${columns.length}, 1fr)`,
            gap: 2,
            mb: 3,
          }}
        >
          {columnData.map((col, index) => (
            <Card key={index} variant="outlined">
              <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1,
                  }}
                >
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Version</InputLabel>
                    <Select
                      value={col.version ?? ''}
                      label="Version"
                      onChange={(e) =>
                        handleVersionChange(index, e.target.value as number)
                      }
                    >
                      {revisions.map((r: RevisionSummary) => (
                        <MenuItem
                          key={r.version}
                          value={r.version}
                          disabled={columns.includes(r.version) && columns[index] !== r.version}
                        >
                          v{r.version}
                          {r.version === currentVersion ? ' (current)' : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {columns.length > 2 && (
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveColumn(index)}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>

                {col.isLoading && <CircularProgress size={20} />}

                {col.revision && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(col.revision.created_at)}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.5 }}
                    >
                      {col.revision.sections.length} sections
                    </Typography>

                    <Button
                      size="small"
                      startIcon={<RestoreIcon />}
                      onClick={() =>
                        handleRevertClick(col.version!, col.revision!)
                      }
                      sx={{ mt: 1 }}
                      disabled={col.version === currentVersion || isReverting}
                    >
                      Revert to v{col.version}
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          ))}
        </Box>

        {/* Template Metadata Comparison */}
        <Paper sx={{ mb: 3, overflow: 'hidden' }}>
          <Box sx={{ bgcolor: 'grey.100', px: 2, py: 1.5 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Template Metadata
            </Typography>
          </Box>
          <Divider />

          {/* Title Row */}
          <Box>
            <Box sx={{ bgcolor: 'grey.50', px: 2, py: 1 }}>
              <Typography variant="body2" fontWeight={500}>
                Title
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: `repeat(${columns.length}, 1fr)`,
                gap: 0,
              }}
            >
              {columnData.map((col, index) => (
                <Box
                  key={index}
                  sx={{
                    p: 2,
                    borderRight:
                      index < columns.length - 1 ? 1 : 0,
                    borderColor: 'divider',
                  }}
                >
                  {col.isLoading ? (
                    <CircularProgress size={16} />
                  ) : col.revision ? (
                    <Typography variant="body2">
                      {col.revision.title}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.disabled">
                      Select a version
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Box>

          <Divider />

          {/* Description Row */}
          <Box>
            <Box sx={{ bgcolor: 'grey.50', px: 2, py: 1 }}>
              <Typography variant="body2" fontWeight={500}>
                Description
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: `repeat(${columns.length}, 1fr)`,
                gap: 0,
              }}
            >
              {columnData.map((col, index) => (
                <Box
                  key={index}
                  sx={{
                    p: 2,
                    borderRight:
                      index < columns.length - 1 ? 1 : 0,
                    borderColor: 'divider',
                    minHeight: 80,
                  }}
                >
                  {col.isLoading ? (
                    <CircularProgress size={16} />
                  ) : col.revision ? (
                    <Typography
                      variant="body2"
                      color={col.revision.description ? 'text.primary' : 'text.disabled'}
                    >
                      {col.revision.description || '(empty)'}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.disabled">
                      —
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Box>

          <Divider />

          {/* Global Prompt Row */}
          <Box>
            <Box sx={{ bgcolor: 'grey.50', px: 2, py: 1 }}>
              <Typography variant="body2" fontWeight={500}>
                Global Prompt
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: `repeat(${columns.length}, 1fr)`,
                gap: 0,
              }}
            >
              {columnData.map((col, index) => (
                <Box
                  key={index}
                  sx={{
                    p: 2,
                    borderRight:
                      index < columns.length - 1 ? 1 : 0,
                    borderColor: 'divider',
                    minHeight: 100,
                    maxHeight: 200,
                    overflow: 'auto',
                  }}
                >
                  {col.isLoading ? (
                    <CircularProgress size={16} />
                  ) : col.revision ? (
                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                      }}
                      color={col.revision.prompt ? 'text.primary' : 'text.disabled'}
                    >
                      {col.revision.prompt || '(empty)'}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.disabled">
                      —
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        </Paper>

        {/* Sections Comparison */}
        <Paper sx={{ overflow: 'hidden' }}>
          <Box sx={{ bgcolor: 'grey.100', px: 2, py: 1.5 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Sections
            </Typography>
          </Box>
          <Divider />

          {allSectionIds.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No sections to compare. Select versions above.
              </Typography>
            </Box>
          ) : (
            allSectionIds.map((sectionId, sectionIndex) => {
              // Find section title from any revision that has it
              const sectionTitle =
                columnData
                  .map((c) => findSection(c.revision, sectionId))
                  .find((s) => s !== null)?.title ?? sectionId;

              return (
                <Box key={sectionId}>
                  {sectionIndex > 0 && <Divider />}
                  <Box sx={{ bgcolor: 'grey.50', px: 2, py: 1 }}>
                    <Typography variant="body2" fontWeight={500}>
                      {sectionTitle}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: `repeat(${columns.length}, 1fr)`,
                      gap: 0,
                    }}
                  >
                    {columnData.map((col, colIndex) => {
                      const section = findSection(col.revision, sectionId);
                      const hasSection = section !== null;

                      return (
                        <Box
                          key={colIndex}
                          sx={{
                            p: 2,
                            borderRight:
                              colIndex < columns.length - 1 ? 1 : 0,
                            borderColor: 'divider',
                            minHeight: 120,
                            display: 'flex',
                            flexDirection: 'column',
                          }}
                        >
                          {col.isLoading ? (
                            <CircularProgress size={16} />
                          ) : hasSection ? (
                            <>
                              <Box
                                sx={{
                                  flex: 1,
                                  maxHeight: 200,
                                  overflow: 'auto',
                                  mb: 1,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    whiteSpace: 'pre-wrap',
                                    fontFamily: 'monospace',
                                    fontSize: '0.75rem',
                                  }}
                                >
                                  {section.content}
                                </Typography>
                              </Box>
                              {col.version !== currentVersion && (
                                isInCart(sectionId) && cart.find(item => item.sectionId === sectionId)?.fromVersion === col.version ? (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="success"
                                    startIcon={<CheckIcon />}
                                    onClick={() => handleRemoveFromCart(sectionId)}
                                    sx={{ alignSelf: 'flex-start' }}
                                  >
                                    In Cart
                                  </Button>
                                ) : (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<AddToCartIcon />}
                                    onClick={() =>
                                      handleAddToCart(
                                        sectionId,
                                        sectionTitle,
                                        col.version!,
                                        section.content,
                                      )
                                    }
                                    sx={{ alignSelf: 'flex-start' }}
                                  >
                                    Add to Cart
                                  </Button>
                                )
                              )}
                            </>
                          ) : (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flex: 1,
                                bgcolor: 'grey.50',
                                borderRadius: 1,
                              }}
                            >
                              <Typography
                                variant="body2"
                                color="text.disabled"
                                fontStyle="italic"
                              >
                                Section not in this version
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              );
            })
          )}
        </Paper>
      </main>

      {/* Revert Modal */}
      <RevertTemplateModal
        open={revertModal.open}
        onClose={() =>
          setRevertModal({ open: false, version: null, revision: null })
        }
        onConfirm={handleRevertConfirm}
        version={revertModal.version}
        revision={revertModal.revision}
        isLoading={isReverting}
      />

      {/* Section Cart Drawer */}
      <SectionCartDrawer
        open={cartDrawerOpen}
        onClose={() => setCartDrawerOpen(false)}
        items={cart}
        onRemoveItem={handleRemoveFromCart}
        onClearCart={handleClearCart}
        onApplyChanges={handleApplyCart}
        isApplying={isReverting}
        currentVersion={currentVersion ?? 0}
      />
    </div>
  );
}
